/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";

import openToast from "utils/openToast";

import useTryCatch from "hooks/useTryCatch";
import { complete2FASetup, getRecoveryCodes } from "lib/v3/loginAPI";
import { useAuth } from "state";

import { RecoveryCodeSkeleton } from "components/common";
import { DownloadIcon } from "components/icons";
import AttentionIcon from "components/icons/AttentionIcon";
import DotIcon from "components/icons/DotIcon";
import Text from "components/ui/Text";
import { Button } from "components/ui/Button";
import Stack from "components/ui/Stack";

const RecoveryCodes = ({ confirmationCode }: { confirmationCode: string }) => {
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { handleLogin } = useAuth();
  const navigate = useNavigate();

  const getRecoveryCodesFun = useTryCatch(
    async () => {
      const { data } = await getRecoveryCodes(confirmationCode);
      setRecoveryCodes(data.recovery_codes);
    },
    {
      setLoading: setIsLoading,
    },
  );

  useEffect(() => {
    getRecoveryCodesFun();
  }, []);

  const downloadRecoverCodesFile = () => {
    const numbersString = recoveryCodes.join("\n");

    const blob = new Blob([numbersString], {
      type: "text/plain;charset=utf-8",
    });

    saveAs(blob, "recovery-codes.txt");
  };

  const completeSetup = useMutation(complete2FASetup);

  const onSubmit = () => {
    setIsLoading(true);

    completeSetup.mutate(undefined, {
      onSuccess: ({ data }) => {
        setIsLoading(false);
        const { token, reactapp_v, setting_id } = data;

        if (token) {
          localStorage.setItem("role", "admin");
          localStorage.setItem("token", token);
        }

        if (reactapp_v) {
          localStorage.setItem("version", reactapp_v);
        }

        if (setting_id) {
          localStorage.setItem("setting_id", setting_id);
        }

        navigate("/");
        handleLogin(data, "admin");
      },
      onError: (error: any) => {
        openToast(error.response.data.error, "error");
      },
    });
  };

  return (
    <form>
      <Stack className="space-y-10">
        <Stack className="space-y-8">
          <Stack>
            <h3 className="text-white text-xl font-bold">You are all set!</h3>
            <Text className="text-base text-[#9A9A9A]">
              Now you can use the mobile authentication app to get an
              authentication code any time you login.
            </Text>
          </Stack>

          <div className="flex gap-[10px] bg-[#B2DDFF] p-[10px]">
            <div>
              <AttentionIcon fill="#194185" />
            </div>

            <Text className="text-sm text-[#194185]">
              Keep your recovery code in a safe place, It lets you log in if you
              lose your device or have trouble accessing your authentication
              app.
            </Text>
          </div>

          <Stack className="space-y-4 w-full">
            <div className="flex flex-wrap border-gray-400 border-solid border-[1px] p-3 gap-y-[10px] ">
              {isLoading ? (
                <RecoveryCodeSkeleton />
              ) : (
                recoveryCodes.map((item: any, index: number) => (
                  <span
                    key={index}
                    className={`w-1/2 flex items-center text-nowrap text-gray-50`}
                  >
                    <DotIcon
                      className={`w-[4px] h-[4px] fill-white mr-[12px] ml-[12px]`}
                    />
                    {item}
                  </span>
                ))
              )}
            </div>

            <Button
              variant="secondary"
              type="submit"
              size="xl"
              onClick={downloadRecoverCodesFile}
              id="downloadQrBtn"
            >
              <DownloadIcon />
              <Text className="ml-3">Download Recovery Codes</Text>
            </Button>
          </Stack>
        </Stack>

        <Button
          variant="primary"
          type="submit"
          size="xl"
          id="nextConfirmBtn"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={onSubmit}
        >
          Complete Setup
        </Button>
      </Stack>
    </form>
  );
};

export default RecoveryCodes;
